import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { vlearnSurface, vlearnType } from "../vlearnUi";

/**
 * V-Learning — a lesson whose content is a downloadable document.
 *
 * There is no inline preview, and that is the design rather than a gap. A .docx or .xlsx
 * cannot be displayed by any browser without a conversion service, and the two ways to fake
 * one — Microsoft's or Google's public viewer — would send the platform's course material to
 * a third party. So the lesson presents the file honestly: what it is, how big it is, and a
 * button that downloads it.
 */

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

/**
 * A file size in the reader's own units.
 *
 * Exported because `SlidesLesson` presents its downloadable source deck the same way, and two
 * copies of a rounding rule are two chances for the same file to be described differently on
 * two screens. `Ko`/`Mo` really are the French symbols, so the units are translated rather
 * than hardcoded.
 */
export function formatVLearnFileSize(bytes) {
    const size = Number(bytes) || 0
    if (size <= 0) return null
    const kilobytes = size / 1024
    if (kilobytes < 1024) return `${Math.max(1, Math.round(kilobytes))} ${Localize('VLEARN_UNIT_KB')}`
    return `${(kilobytes / 1024).toFixed(1)} ${Localize('VLEARN_UNIT_MB')}`
}

export default function DocumentLesson({ lesson }) {

    const documentFile = ((lesson && lesson.files) || [])
        .find(file => file.kind === 'document') || null

    if (!documentFile) {
        return (
            <Box sx={{ ...vlearnSurface.panel }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_DOCUMENT_MISSING" />
                </Typography>
            </Box>
        )
    }

    const url = NetworkManager.getInstance().getVLearnFileUrl(documentFile.id)
    const size = formatVLearnFileSize(documentFile.size)

    return (
        <Box sx={{ ...vlearnSurface.panel, maxWidth: 640 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{
                    width: 46, height: 46, borderRadius: '12px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(17, 121, 190, 0.10)', color: '#1179be'
                }} aria-hidden="true">
                    <DescriptionOutlinedIcon />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ ...vlearnType.cardTitle, wordBreak: 'break-word' }}>
                        {documentFile.original_name}
                    </Typography>
                    <Box component="dl" sx={{ m: 0, mt: 1, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.5 }}>
                        {size ? (
                            <>
                                <Typography component="dt" sx={{ ...vlearnType.meta, fontWeight: 600 }}>
                                    <Langs str="VLEARN_FILE_SIZE" />
                                </Typography>
                                <Typography component="dd" sx={{ ...vlearnType.meta, m: 0 }}>{size}</Typography>
                            </>
                        ) : null}
                        {documentFile.mime ? (
                            <>
                                <Typography component="dt" sx={{ ...vlearnType.meta, fontWeight: 600 }}>
                                    <Langs str="VLEARN_FILE_TYPE" />
                                </Typography>
                                <Typography component="dd" sx={{ ...vlearnType.meta, m: 0, wordBreak: 'break-all' }}>
                                    {documentFile.mime}
                                </Typography>
                            </>
                        ) : null}
                    </Box>
                </Box>
            </Box>

            <Typography sx={{ ...vlearnType.body, mt: 2 }}>
                <Langs str="VLEARN_DOCUMENT_NO_PREVIEW" />
            </Typography>

            <Button
                variant="contained"
                size="small"
                component="a"
                href={url}
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                sx={{
                    ...focusRing, mt: 2, textTransform: 'none', fontSize: '0.82rem',
                    backgroundColor: 'var(--orange, rgb(255,144,21))',
                    '&:hover': { backgroundColor: '#e07f0d' }
                }}
            >
                <Langs str="VLEARN_DOWNLOAD" />
            </Button>
        </Box>
    )
}
